import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { FieldContext } from './fieldContext'

export interface FieldProps {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  /** Right side of the label row, e.g. "Not registered" link or a unit. */
  aside?: ReactNode
  children: ReactNode
  className?: string
  /** Supply when the control needs a stable id (tests, external labels). */
  id?: string
}

/**
 * Label + control + hint/error, wired for screen readers. The child control (Input, KgInput,
 * Select, ScanField…) picks up id / aria-describedby / aria-invalid from context.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  aside,
  children,
  className,
  id,
}: FieldProps) {
  const auto = useId()
  const controlId = id ?? `f${auto}`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <FieldContext.Provider
      value={{ id: controlId, describedBy, invalid: Boolean(error), required }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className="flex items-end justify-between gap-2">
          <label htmlFor={controlId} className="text-[14px] font-semibold text-ink">
            {label}
            {required && (
              <span className="ml-0.5 text-danger" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {aside && <div className="text-[12px] text-muted">{aside}</div>}
        </div>
        {children}
        {error ? (
          <p id={errorId} className="text-[13px] font-medium text-danger">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="text-[13px] text-muted">
              {hint}
            </p>
          )
        )}
      </div>
    </FieldContext.Provider>
  )
}
